import { useEffect, useState } from "react";

import {
  PaymentsStatusChangesService,
  type PaymentOperationType,
  type PaymentStatusChange,
} from "@/modules/dashboard/payments-activity/services/paymentsStatusChanges.service";

export interface UseOperationStatusTrailResult {
  changes: PaymentStatusChange[] | null;
  isLoading: boolean;
  hasError: boolean;
}

interface TrailSnapshot {
  key: string;
  changes: PaymentStatusChange[] | null;
  failed: boolean;
}

/**
 * Append-only status history of the selected operation (`GET /internal/payments/status-changes`, `seq ASC`).
 *
 * Same guard as the ledger endpoint (`payments:read` + INTERNAL), so on this surface it costs no extra access
 * check. Shown next to the journal entries because that pairing is the whole point of a reconciliation
 * screen: the status says when the runtime decided something, the entries say what it did to the money.
 *
 * `refreshToken` — the caller passes the operation's live status — re-reads the log so a new transition
 * appears without this hook polling on its own. It is deliberately NOT part of the snapshot key: a status
 * flip keeps the previous list on screen while the new one loads, instead of blanking the section.
 */
export const useOperationStatusTrail = (
  operationType: PaymentOperationType | null,
  operationId: string | null,
  refreshToken: string | null,
): UseOperationStatusTrailResult => {
  const [snapshot, setSnapshot] = useState<TrailSnapshot | null>(null);

  const key = operationType && operationId ? `${operationType}|${operationId}` : null;
  // Derived, not synchronized: a snapshot of another operation simply stops matching the key.
  const current = snapshot?.key === key ? snapshot : null;

  useEffect(() => {
    if (!operationType || !operationId) return;
    const snapshotKey = `${operationType}|${operationId}`;
    let cancelled = false;
    PaymentsStatusChangesService.listByOperation(operationType, operationId)
      .then((res) => {
        if (!cancelled) setSnapshot({ key: snapshotKey, changes: res.data, failed: false });
      })
      .catch(() => {
        // An enhancement, not the subject of the page: a failure hides the section instead of breaking it.
        if (!cancelled) setSnapshot({ key: snapshotKey, changes: null, failed: true });
      });
    return () => {
      cancelled = true;
    };
  }, [operationType, operationId, refreshToken]);

  return {
    changes: current?.changes ?? null,
    isLoading: key !== null && current === null,
    hasError: current?.failed ?? false,
  };
};
